// A follow-up's plan, at the top of its activity record page: what it proposes (the step, the
// message and why), the research the reasoning cites, and the touches that led to it. Data from
// GET /api/sales/follow-ups/:id; the draft is written by the drafting routine
// (docs/gtm-follow-ups.md §4), so a follow-up without one shows why its rule opened it.
import {
  Button, Card, CardBody, CardHeader, DetailSections, InlineMessage, Spinner, Text, TextLink,
  type RecordDetailSection,
} from '@olytic/foundation';
import { Copy } from 'lucide-react';
import { type ReactNode, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import { authenticatedFetch } from '../auth/authClient';
import { recordPath } from '../objects/routes';
import {
  fetchFollowUpDetail, followUpReason, localStamp, paragraphs, touchKind,
  type DraftEvidence, type FollowUpDetail,
} from './sellerHome';

function RecordLink({ to, children }: { to: string; children: ReactNode }) {
  return <TextLink asChild><Link to={to}>{children}</Link></TextLink>;
}

/** Text with its paragraphs and line breaks kept. */
function Prose({ text }: { text: string | null | undefined }) {
  return (
    <div className="flex flex-col gap-3">
      {paragraphs(text).map((lines, i) => (
        <Text key={i} size="sm">{lines.map((line, j) => <span key={j}>{j > 0 ? <br /> : null}{line}</span>)}</Text>
      ))}
    </div>
  );
}

function Evidence({ items }: { items: DraftEvidence[] }) {
  return (
    <ul className="oly-reset m-0 flex list-none flex-col gap-4 p-0">
      {items.map((e) => (
        <li key={e.finding_id} className="flex flex-col gap-1">
          {e.use ? <Text size="sm">{e.use}</Text> : null}
          <Text size="sm" tone="muted">
            {e.statement}{' '}
            <RecordLink to={recordPath('research-findings', e.finding_id)}>
              {[e.source.title, e.source.publisher, e.source.published_on?.slice(0, 4)].filter(Boolean).join(', ')}
            </RecordLink>
            {e.source.quality_grade ? ` · Grade ${e.source.quality_grade}` : ''}
          </Text>
        </li>
      ))}
    </ul>
  );
}

/** The plan is one column: every field spans it. */
const wide = (sections: RecordDetailSection[]): RecordDetailSection[] =>
  sections.map((s) => ({ ...s, fields: s.fields.map((field) => ({ ...field, wide: true })) }));

function sectionsFor(f: FollowUpDetail): RecordDetailSection[] {
  const draft = f.draft;
  const sections: RecordDetailSection[] = [{
    key: 'proposal',
    label: 'What To Do',
    fields: [
      { key: 'action', label: 'Next Step', value: draft?.action ?? followUpReason(f) },
      ...(draft?.subject ? [{ key: 'draft_subject', label: 'Email Subject', value: draft.subject }] : []),
      {
        key: 'message',
        label: 'Suggested Message',
        value: draft?.body ? <Prose text={draft.body} /> : 'Not drafted yet. Drafts are written each morning for follow-ups due in the next week.',
      },
      ...(f.body ? [{ key: 'notes', label: 'Notes', value: <Prose text={f.body} /> }] : []),
    ],
  }];
  if (draft?.why) {
    sections.push({ key: 'why', label: 'Why This Step', fields: [{ key: 'why', label: 'Reasoning', value: <Prose text={draft.why} /> }] });
  }
  if (draft?.evidence.length) {
    sections.push({ key: 'research', label: 'Research Behind It', fields: [{ key: 'evidence', label: 'Findings', value: <Evidence items={draft.evidence} /> }] });
  }
  if (f.thread.length) {
    sections.push({
      key: 'thread',
      label: 'How We Got Here',
      fields: f.thread.map((t) => ({
        key: `touch:${t.id}`,
        label: <RecordLink to={recordPath('activities', t.id)}>{`${touchKind(t.channel, t.direction)} · ${localStamp(t.occurred_at)}`}</RecordLink>,
        value: <Prose text={t.body ?? t.summary ?? t.subject} />,
      })),
    });
  }
  return wide(sections);
}

export function FollowUpPlan({ id }: { id: string }) {
  const [detail, setDetail] = useState<FollowUpDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    let live = true;
    fetchFollowUpDetail(authenticatedFetch, id).then(
      (d) => { if (live) setDetail(d); },
      (e: unknown) => { if (live) setError(e instanceof Error ? e.message : 'Could not load this follow-up.'); },
    );
    return () => { live = false; };
  }, [id]);

  const message = detail?.draft?.body;
  return (
    <Card>
      <CardHeader
        title="Follow-Up Plan"
        actions={message ? (
          <Button variant="brand" size="sm" onClick={() => { void navigator.clipboard?.writeText(message).then(() => setCopied(true)); }}>
            <Copy aria-hidden /> {copied ? 'Copied' : 'Copy Message'}
          </Button>
        ) : undefined}
      />
      <CardBody>
        {error ? <InlineMessage tone="error" role="alert">{error}</InlineMessage> : null}
        {detail === null && !error ? <div className="flex justify-center py-6"><Spinner label="Loading the follow-up plan" /></div> : null}
        {detail ? <DetailSections sections={sectionsFor(detail)} /> : null}
      </CardBody>
    </Card>
  );
}
